class TabsAccordion extends HTMLElement {
  constructor() {
    super();
    this.buttons = this.querySelectorAll('[data-tab-button]');
    this.contents = this.querySelectorAll('[data-tab-content]');
    this.design = this.dataset.design;
    this.tabsLayout = this.dataset.tabsLayout;
    this.mobileAccordion = this.dataset.mobileAccordion === 'true';
    
    this.init();
    this.setupResizeHandler();
  }

  init() {
    this.buttons.forEach(button => {
      button.addEventListener('click', () => this.toggleTab(button));
    });

    // Set first tab open if none are set to open by default
    const hasOpenTab = Array.from(this.buttons).some(button => 
      button.getAttribute('aria-expanded') === 'true'
    );
    
    if (!hasOpenTab && this.buttons.length > 0) {
      if (this.design === 'accordion' || (this.mobileAccordion && this.isMobile())) {
        this.openTab(this.buttons[0]);
      } else if (this.design === 'tabs') {
        this.openTab(this.buttons[0]);
      }
    }
  }

  toggleTab(button) {
    const isExpanded = button.getAttribute('aria-expanded') === 'true';
    const isAccordionMode = this.design === 'accordion' || (this.mobileAccordion && this.isMobile());
    
    if (isAccordionMode) {
      // Accordion behavior - toggle single item
      if (isExpanded) {
        this.closeTab(button);
      } else {
        this.closeAllTabs();
        this.openTab(button);
      }
    } else {
      // Tabs behavior - close all, open clicked
      this.closeAllTabs();
      this.openTab(button);
    }
  }

  openTab(button) {
    const contentId = button.getAttribute('aria-controls');
    const content = document.getElementById(contentId);
    
    if (!content) return;
    
    button.setAttribute('aria-expanded', 'true');
    content.setAttribute('aria-hidden', 'false');
    
    // Use requestAnimationFrame for smooth animation
    requestAnimationFrame(() => {
      content.style.display = 'block';
    });
  }

  closeTab(button) {
    const contentId = button.getAttribute('aria-controls');
    const content = document.getElementById(contentId);
    
    if (!content) return;
    
    button.setAttribute('aria-expanded', 'false');
    content.setAttribute('aria-hidden', 'true');
    content.style.display = 'none';
  }

  closeAllTabs() {
    this.buttons.forEach(button => {
      this.closeTab(button);
    });
  }

  isMobile() {
    return window.innerWidth < 768;
  }

  handleResize() {
    // Re-initialize if switching between mobile and desktop
    if (this.mobileAccordion) {
      const isCurrentlyMobile = this.isMobile();
      
      if (isCurrentlyMobile !== this.lastMobileState) {
        this.lastMobileState = isCurrentlyMobile;
        
        if (isCurrentlyMobile) {
          // On mobile, close all tabs except first if none open
          const hasOpenTab = Array.from(this.buttons).some(button => 
            button.getAttribute('aria-expanded') === 'true'
          );
          
          if (!hasOpenTab && this.buttons.length > 0) {
            this.openTab(this.buttons[0]);
          }
        } else {
          // On desktop, close all tabs except first if in tabs mode
          if (this.design === 'tabs') {
            const hasOpenTab = Array.from(this.buttons).some(button => 
              button.getAttribute('aria-expanded') === 'true'
            );
            
            if (!hasOpenTab && this.buttons.length > 0) {
              this.openTab(this.buttons[0]);
            }
          }
        }
      }
    }
  }

  setupResizeHandler() {
    this.lastMobileState = this.isMobile();
    
    const resizeHandler = () => {
      this.handleResize();
    };
    
    window.addEventListener('resize', resizeHandler);
    
    // Store handler for cleanup if needed
    this.resizeHandler = resizeHandler;
  }

  disconnectedCallback() {
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
  }
}

customElements.define('tabs-accordion-component', TabsAccordion);

// Initialize all tabs-accordion components on page load
if (typeof window !== 'undefined') {
  document.addEventListener('DOMContentLoaded', () => {
    // Components are auto-initialized by the custom element
  });
}